import { useEffect, useRef, useState, type FormEvent } from 'react'
import type { Action, ActionDraft, ActionRepeatMode, ActionType } from '../../domain/action'
import { actionStatsLabel, actionTypeLabel } from '../../domain/action'
import type { Specialization } from '../../domain/specialization'
import { AddIcon, CheckCircleIcon, CheckIcon, CloseIcon, CounterIcon, RepeatIcon, TimerIcon, TrophyIcon } from './Icons'
import { useI18n } from '../../i18n'

interface ActionSheetProps {
  action: Action | null
  specializations: Specialization[]
  initialObjectiveId?: string | null
  areaColor: string
  open: boolean
  saving: boolean
  error: string | null
  onClose: () => void
  onSave: (draft: ActionDraft) => Promise<boolean>
  onDelete?: () => Promise<boolean>
}

const types: Array<{ value: ActionType; Icon: typeof CheckCircleIcon }> = [
  { value: 'simple', Icon: CheckCircleIcon },
  { value: 'timer', Icon: TimerIcon },
  { value: 'counter', Icon: CounterIcon },
]

export const ActionSheet = ({ action, specializations, initialObjectiveId, areaColor, open, saving, error, onClose, onSave, onDelete }: ActionSheetProps) => {
  const { language, copy: { common, action: actionCopy, actionSheet } } = useI18n()
  const dialogRef = useRef<HTMLDialogElement>(null)
  const [name, setName] = useState('')
  const [type, setType] = useState<ActionType>('simple')
  const [repeatMode, setRepeatMode] = useState<ActionRepeatMode>('repeatable')
  const [xpText, setXpText] = useState('50')
  const [coinText, setCoinText] = useState('10')
  const [selectedCategoryIds, setSelectedCategoryIds] = useState<Set<string>>(new Set())
  const [addingSpecialization, setAddingSpecialization] = useState(false)
  const [newSpecialization, setNewSpecialization] = useState('')
  const [createdSpecializationName, setCreatedSpecializationName] = useState<string | null>(null)
  const [confirmDelete, setConfirmDelete] = useState(false)

  useEffect(() => {
    const dialog = dialogRef.current
    if (!dialog) return
    if (open && !dialog.open) dialog.showModal()
    if (!open && dialog.open) dialog.close()
  }, [open])

  useEffect(() => {
    if (!open) return
    setName(action?.name ?? '')
    setType(action?.type ?? 'simple')
    setRepeatMode(action?.repeatMode ?? (initialObjectiveId ? 'once' : 'repeatable'))
    setXpText((action?.xp ?? 50).toString())
    setCoinText((action?.coins ?? 10).toString())
    setSelectedCategoryIds(new Set(action?.categoryIds ?? []))
    setAddingSpecialization(false)
    setNewSpecialization('')
    setCreatedSpecializationName(null)
    setConfirmDelete(false)
  }, [action, initialObjectiveId, open])

  const xp = Number.parseInt(xpText, 10)
  const coins = Number.parseInt(coinText, 10)
  const valid = name.trim().length > 0 && Number.isInteger(xp) && xp >= 1 && Number.isInteger(coins) && coins >= 0 && (!addingSpecialization || newSpecialization.trim().length > 0)
  const unit = type === 'timer' ? actionSheet.perMinute : type === 'counter' ? actionSheet.perRep : actionSheet.onComplete

  const submit = async (event: FormEvent) => {
    event.preventDefault()
    if (!valid) return
    await onSave({ name: name.trim(), type, repeatMode, xp, coins, categoryIds: [...selectedCategoryIds], objectiveIds: action?.objectiveIds ?? (initialObjectiveId ? [initialObjectiveId] : []), newSpecializationName: action ? null : createdSpecializationName ?? (addingSpecialization ? newSpecialization.trim() : null) })
  }

  return (
    <dialog ref={dialogRef} className="sheet action-sheet" onCancel={(event) => { event.preventDefault(); onClose() }} onClose={onClose}>
      <form className="sheet__body action-form" onSubmit={submit}>
        <div className="sheet__handle" />
        <div className="sheet__heading">
          <div>
            <h2>{action ? actionSheet.edit : actionSheet.create}</h2>
            <p>{action ? actionStatsLabel(action, language) : actionSheet.define}</p>
          </div>
          <button className="icon-button" type="button" onClick={onClose} aria-label={common.close}><CloseIcon /></button>
        </div>

        <label className="field">
          <span>{common.name}</span>
          <input value={name} onChange={(event) => setName(event.target.value)} placeholder={actionSheet.example} maxLength={80} autoFocus />
        </label>

        <fieldset className="form-section">
          <legend>{actionSheet.skills}</legend>
          <p className="form-helper">{action ? actionSheet.assignEdit : actionSheet.assignCreate}</p>
          <div className="category-chips">
            <button type="button" aria-pressed={selectedCategoryIds.size === 0 && !createdSpecializationName && !addingSpecialization} className={selectedCategoryIds.size === 0 && !createdSpecializationName && !addingSpecialization ? 'category-chip category-chip--selected' : 'category-chip'} onClick={() => { setSelectedCategoryIds(new Set()); setCreatedSpecializationName(null); setAddingSpecialization(false); setNewSpecialization('') }}>{common.noSkill}</button>
            {specializations.map((item) => <button key={item.id} type="button" aria-pressed={selectedCategoryIds.has(item.id)} className={selectedCategoryIds.has(item.id) ? 'category-chip category-chip--selected' : 'category-chip'} onClick={() => { setSelectedCategoryIds((current) => { const next = new Set(current); if (next.has(item.id)) next.delete(item.id); else next.add(item.id); return next }); setAddingSpecialization(false) }}>{item.name}</button>)}
            {!action && createdSpecializationName && <button type="button" className="category-chip category-chip--selected" onClick={() => { setCreatedSpecializationName(null); setNewSpecialization('') }}>{createdSpecializationName}</button>}
            {!action && <button type="button" className="category-chip category-chip--new" onClick={() => setAddingSpecialization(true)}><AddIcon />{actionSheet.newSkill}</button>}
          </div>
          {!action && addingSpecialization && <div className="inline-specialization"><input value={newSpecialization} onChange={(event) => setNewSpecialization(event.target.value.slice(0, 30))} placeholder={actionSheet.newSkill} /><button className="icon-button" type="button" disabled={!newSpecialization.trim()} onClick={() => { setCreatedSpecializationName(newSpecialization.trim()); setAddingSpecialization(false) }} aria-label={actionSheet.useSkill}><CheckIcon /></button></div>}
          {action && specializations.length === 0 && <p className="form-helper">{actionSheet.createFirstSkill}</p>}
        </fieldset>

        <fieldset className="form-section">
          <legend>{actionSheet.tracking}</legend>
          {action ? (
            <div className="fixed-type"><strong>{actionTypeLabel(action.type, language)}</strong><small>{actionSheet.fixed}</small></div>
          ) : (
            <div className="segmented segmented--three">
              {types.map(({ value, Icon }) => (
                <button key={value} type="button" aria-pressed={type === value} className={type === value ? 'segment segment--selected' : 'segment'} onClick={() => setType(value)}>
                  <Icon /><span>{actionTypeLabel(value, language)}</span>
                </button>
              ))}
            </div>
          )}
        </fieldset>

        <fieldset className="form-section">
          <legend>{actionSheet.repetition}</legend>
          <div className="segmented">
            <button type="button" aria-pressed={repeatMode === 'repeatable'} className={repeatMode === 'repeatable' ? 'segment segment--selected' : 'segment'} onClick={() => setRepeatMode('repeatable')}><RepeatIcon /><span>{actionSheet.repeatable}</span></button>
            <button type="button" aria-pressed={repeatMode === 'once'} className={repeatMode === 'once' ? 'segment segment--selected' : 'segment'} onClick={() => setRepeatMode('once')}><TrophyIcon /><span>{actionSheet.once}</span></button>
          </div>
          {repeatMode === 'once' && <p className="unique-note">{actionSheet.onceHelp}</p>}
        </fieldset>

        <div className="reward-fields">
          <label className="field"><span>XP {unit}</span><input inputMode="numeric" value={xpText} onChange={(event) => setXpText(event.target.value.replace(/\D/g, '').slice(0, 7))} /></label>
          <label className="field"><span>{actionSheet.coins} {unit}</span><input inputMode="numeric" value={coinText} onChange={(event) => setCoinText(event.target.value.replace(/\D/g, '').slice(0, 7))} /></label>
        </div>
        <p className="sheet__note">{actionSheet.trust}</p>

        {error && <div className="error-message" role="alert">{error}</div>}

        {action && onDelete && (
          confirmDelete ? (
            <div className="delete-confirm">
              <p>{common.delete} “{action.name}”? {actionSheet.deleteConfirm}</p>
              <div><button type="button" className="button button--ghost" onClick={() => setConfirmDelete(false)}>{common.cancel}</button><button type="button" className="button button--danger" disabled={saving} onClick={() => void onDelete()}>{common.delete}</button></div>
            </div>
          ) : <button type="button" className="delete-link" disabled={saving} onClick={() => setConfirmDelete(true)}>{actionSheet.deleteAction}</button>
        )}

        <button className="button button--primary" type="submit" disabled={!valid || saving} style={{ backgroundColor: areaColor }}>
          {saving ? common.saving : action ? common.saveChanges : actionSheet.createAction}
        </button>
      </form>
    </dialog>
  )
}
